<script setup lang="ts">
  // Framework
  import { Atom, useBreakpoints, useTheme } from '@vuetify/v0'

  // Components
  import { Discovery } from '@/components/discovery'

  // Composables
  import { useNavigation } from '@/composables/useNavigation'
  import { useSearch } from '@/composables/useSearch'
  import { useSettings } from '@/composables/useSettings'

  // Stores
  import { useAuthStore } from '@vuetify/auth'

  // Utilities
  import { toRef } from 'vue'
  import { useRoute } from 'vue-router'

  // Types
  import type { AtomProps } from '@vuetify/v0'

  const { as = 'header' } = defineProps<AtomProps>()

  const auth = useAuthStore()
  const navigation = useNavigation()
  const route = useRoute()

  const isHomePage = toRef(() => route.path === '/')

  const breakpoints = useBreakpoints()
  const search = useSearch()
  const settings = useSettings()
  const theme = useTheme()

  const darkLogo = 'https://cdn.vuetifyjs.com/docs/images/logos/vzero-logo-dark.svg'
  const lightLogo = 'https://cdn.vuetifyjs.com/docs/images/logos/vzero-logo-light.svg'
</script>

<template>
  <Atom
    :as
    :class="['flex items-center justify-between h-[48px] fixed inset-x-0 top-[var(--app-banner-h,0px)] px-2 min-[430px]:px-3 text-on-surface border-b border-solid border-divider z-1', settings.showBgGlass.value ? 'bg-glass-surface' : 'bg-surface']"
    data-app-bar
  >
    <div class="flex items-center gap-2">
      <router-link class="inline-block w-[128px]" to="/">
        <img
          v-if="!theme.isDark.value"
          alt="Vuetify0 Logo"
          class="logo-light"
          decoding="async"
          fetchpriority="high"
          height="52"
          :src="lightLogo"
          width="128"
        >

        <img
          v-else
          alt="Vuetify0 Logo"
          class="logo-dark"
          decoding="async"
          fetchpriority="high"
          height="52"
          :src="darkLogo"
          width="128"
        >
      </router-link>

      <button
        v-if="!isHomePage"
        :aria-expanded="navigation.isOpen.value"
        :aria-label="navigation.isOpen.value ? 'Close navigation' : 'Open navigation'"
        class="pa-1 cursor-pointer md:hidden bg-transparent border-0 inline-flex align-center"
        type="button"
        @click="navigation.toggle()"
      >
        <AppIcon :icon="navigation.isOpen.value ? 'close' : 'menu'" />
      </button>

      <Discovery.Activator class="rounded-2xl" step="search">
        <button
          aria-label="Search (Ctrl+K)"
          :class="['inline-flex items-center gap-1.5 rounded-full md:border md:border-divider md:pl-1.5 md:pr-1.5 md:py-1.5 hover:border-primary/50 transition-colors', settings.showBgGlass.value ? 'md:bg-glass-surface' : 'md:bg-surface']"
          type="button"
          @click="search.focus()"
        >
          <span class="shrink-0 size-6 rounded-full bg-primary text-on-primary flex items-center justify-center">
            <AppIcon icon="search" size="12" />
          </span>

          <span class="hidden md:inline text-sm text-on-surface-variant">Search the docs...</span>
          <kbd class="hidden md:inline-flex shrink-0 px-1.5 py-0.5 rounded bg-surface-tint text-on-surface-tint text-[10px] font-mono items-center rounded-r-lg">Ctrl+K</kbd>
        </button>
      </Discovery.Activator>
    </div>

    <div class="flex align-center items-center gap-2 min-[430px]:gap-3">
      <AppSkillFilter v-if="!isHomePage && settings.showSkillFilter.value && breakpoints.width.value >= 440" />

      <AppThemeToggle v-if="isHomePage || settings.showThemeToggle.value" />

      <AppTooltip
        v-if="isHomePage || settings.showSocialLinks.value"
        aria-label="Discord Community (opens in new tab)"
        as="a"
        class="bg-[#5865F2] text-white pa-1 hidden min-[430px]:inline-flex rounded opacity-90 hover:opacity-100"
        href="https://discord.gg/vuetify"
        position-area="bottom"
        rel="noopener noreferrer"
        target="_blank"
        text="Discord Community"
      >
        <AppIcon class="!opacity-100" icon="discord" />
      </AppTooltip>

      <AppTooltip
        v-if="isHomePage || settings.showSocialLinks.value"
        aria-label="GitHub Repository (opens in new tab)"
        as="a"
        class="bg-[#24292f] text-white pa-1 hidden min-[430px]:inline-flex rounded opacity-90 hover:opacity-100"
        href="https://github.com/vuetifyjs/0"
        position-area="bottom"
        rel="noopener noreferrer"
        target="_blank"
        text="GitHub Repository"
      >
        <AppIcon class="!opacity-100" icon="github" />
      </AppTooltip>

      <AppAccount v-if="!isHomePage" />

      <Discovery.Activator v-if="!isHomePage && !auth.isAuthenticated" class="rounded-lg" step="settings">
        <AppSettings />
      </Discovery.Activator>
    </div>
  </Atom>
</template>
